import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Activity,
  ArrowRight,
  BrainCircuit,
  Compass,
  HeartHandshake,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import PublicSiteHeader from '../components/public/PublicSiteHeader';
import { useLanguage } from '../contexts/LanguageContext';

const FeaturesPage = () => {
  const { t } = useLanguage();

  const sections = [
    {
      icon: Activity,
      title: t('marketing.details.trainingTitle'),
      description: t('marketing.details.trainingDescription'),
      body: t('marketing.details.trainingBody'),
    },
    {
      icon: HeartHandshake,
      title: t('marketing.details.coachingTitle'),
      description: t('marketing.details.coachingDescription'),
      body: t('marketing.details.coachingBody'),
    },
    {
      icon: BrainCircuit,
      title: t('marketing.details.aiTitle'),
      description: t('marketing.details.aiDescription'),
      body: t('marketing.details.aiBody'),
    },
    {
      icon: Users,
      title: t('marketing.details.communityTitle'),
      description: t('marketing.details.communityDescription'),
      body: t('marketing.details.communityBody'),
    },
  ];

  return (
    <div className='min-h-screen bg-background text-foreground'>
      <PublicSiteHeader />

      <main className='mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20'>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          className='grid gap-8 rounded-[2rem] border border-border/60 bg-[linear-gradient(180deg,rgba(10,36,88,0.06),rgba(10,36,88,0.02))] p-8 shadow-sm lg:grid-cols-[1.1fr_0.9fr]'
        >
          <div>
            <p className='text-sm font-semibold uppercase tracking-[0.24em] text-[hsl(var(--brand-electric))]'>
              {t('marketing.details.eyebrow')}
            </p>
            <h1 className='mt-4 text-4xl font-semibold tracking-tight sm:text-5xl'>
              {t('marketing.details.title')}
            </h1>
            <p className='mt-5 max-w-2xl text-base leading-7 text-muted-foreground'>
              {t('marketing.details.description')}
            </p>
          </div>

          <Card className='border-border/60 bg-card/90 shadow-sm'>
            <CardContent className='p-6'>
              <div className='flex items-center gap-3'>
                <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(var(--brand-electric-soft))]/15 text-[hsl(var(--brand-navy))]'>
                  <Compass className='h-5 w-5' />
                </div>
                <div>
                  <p className='text-sm font-medium text-muted-foreground'>
                    {t('marketing.details.summaryEyebrow')}
                  </p>
                  <h2 className='text-xl font-semibold'>{t('marketing.details.summaryTitle')}</h2>
                </div>
              </div>
              <p className='mt-5 text-sm leading-7 text-muted-foreground'>
                {t('marketing.details.summaryDescription')}
              </p>
              <div className='mt-6 flex flex-col gap-3 sm:flex-row'>
                <Button asChild className='rounded-full bg-[hsl(var(--brand-navy))] text-white hover:bg-[hsl(var(--brand-navy-deep))]'>
                  <Link to='/login2'>
                    {t('marketing.cta.primary')}
                    <ArrowRight className='ml-2 h-4 w-4' />
                  </Link>
                </Button>
                <Button asChild variant='outline' className='rounded-full'>
                  <a href='/#community'>{t('marketing.details.summarySecondary')}</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <div className='mt-10 grid gap-6 lg:grid-cols-2'>
          {sections.map(({ icon: Icon, title, description, body }, index) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
            >
              <Card className='h-full border-border/60 bg-card shadow-sm'>
                <CardContent className='space-y-4 p-7'>
                  <div className='flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(var(--brand-electric-soft))]/15 text-[hsl(var(--brand-navy))]'>
                    <Icon className='h-5 w-5' />
                  </div>
                  <div>
                    <h2 className='text-2xl font-semibold text-card-foreground'>{title}</h2>
                    <p className='mt-2 text-base font-medium text-[hsl(var(--brand-navy))]'>
                      {description}
                    </p>
                    <p className='mt-4 text-sm leading-7 text-muted-foreground'>{body}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default FeaturesPage;
